/**
 * Dashboard 자동 정렬 및 크기 조정 기능
 * 이 파일은 Dashboard 컴포넌트에서 사용하는 자동 정렬 및 크기 조정 관련 함수들을 담고 있습니다.
 */

/**
 * 컴포넌트 위치를 자동으로 정렬하는 함수
 */
export const autoArrangeLayout = (
  sortOption = 'position',
  dashboardTemplate,
  layouts,
  setLayouts,
  editMode,
  showSuccessMessage,
  showErrorMessage
) => {
  if (!dashboardTemplate || !dashboardTemplate.components || !layouts.lg) {
    console.error("[Dashboard] 레이아웃 자동 정렬을 위한 컴포넌트가 없습니다.");
    return;
  }
  
  try {
    // 현재 레이아웃 항목 복제
    const currentLayoutItems = [...layouts.lg];
    
    // 컴포넌트 정보 매핑 - 타이틀 등 추가 정보를 위해
    const componentsMap = {};
    dashboardTemplate.components.forEach(comp => {
      componentsMap[comp.id] = comp;
    });
    
    // 고정된 컴포넌트와 이동 가능한 컴포넌트 분리
    const lockedItems = [];
    const movableItems = [];
    
    currentLayoutItems.forEach(item => {
      const component = componentsMap[item.i];
      if (component && component.layout_locked === true) {
        lockedItems.push(item);
      } else {
        movableItems.push(item);
      }
    });
    
    // 이동 가능한 컴포넌트만 정렬 방식에 따라 정렬
    let sortedItems;
    switch (sortOption) {
      case 'size_asc': // 작은 컴포넌트부터 (면적 기준)
        sortedItems = [...movableItems].sort((a, b) => (a.w * a.h) - (b.w * b.h));
        break;
      case 'size_desc': // 큰 컴포넌트부터 (면적 기준)
        sortedItems = [...movableItems].sort((a, b) => (b.w * b.h) - (a.w * a.h));
        break;
      case 'width': // 너비가 넓은 순
        sortedItems = [...movableItems].sort((a, b) => b.w - a.w);
        break;
      case 'height': // 높이가 높은 순
        sortedItems = [...movableItems].sort((a, b) => b.h - a.h);
        break;
      case 'title': // 타이틀 기준 정렬
        sortedItems = [...movableItems].sort((a, b) => {
          const titleA = componentsMap[a.i]?.title || '';
          const titleB = componentsMap[b.i]?.title || '';
          return titleA.localeCompare(titleB);
        });
        break;
      case 'position': // 기본 - 위치 기준 (y, x 순)
      default:
        sortedItems = [...movableItems].sort((a, b) => {
          if (a.y === b.y) return a.x - b.x;
          return a.y - b.y;
        });
        break;
    }
    
    // 그리드 레이아웃 속성 - 템플릿에서 동적으로 가져옴
    const cols = dashboardTemplate?.layout?.columns || 12;
    
    // 새 레이아웃 생성
    const newLayout = [];
    
    // 각 행의 높이를 추적하는 맵
    const rowHeights = new Map();
    
    // 각 셀의 점유 상태를 저장하는 그리드 맵
    const gridOccupancyMap = [];
    for (let y = 0; y < 100; y++) { // 충분히 큰 행 수
      gridOccupancyMap[y] = new Array(cols).fill(false);
    }
    
    // 고정된 컴포넌트들을 먼저 그리드 맵에 표시하고 새 레이아웃에 추가
    lockedItems.forEach(item => {
      // 고정된 컴포넌트는 기존 위치 그대로 유지
      newLayout.push({ ...item });
      
      // 고정된 컴포넌트가 점유하는 영역을 그리드 맵에 표시하지 않음 (투명 취급)
      // for (let dy = 0; dy < item.h; dy++) {
      //   for (let dx = 0; dx < item.w; dx++) {
      //     const y = item.y + dy;
      //     const x = item.x + dx;
      //     if (y < 100 && x < cols) { // 범위 체크
      //       gridOccupancyMap[y][x] = true;
      //     }
      //   }
      // }
      
      // 고정된 컴포넌트의 행 높이 정보도 업데이트하지 않음 (투명 취급)
      // for (let dy = 0; dy < item.h; dy++) {
      //   const rowY = item.y + dy;
      //   if (!rowHeights.has(rowY) || rowHeights.get(rowY) < item.x + item.w) {
      //     rowHeights.set(rowY, item.x + item.w);
      //   }
      // }
    });
    
    // 이동 가능한 컴포넌트들을 배치할 수 있는 최적의 위치 찾기 (고정된 컴포넌트는 무시)
    sortedItems.forEach(item => {
      const itemWidth = item.w;
      const itemHeight = item.h;
      
      // 가능한 모든 시작 위치 확인
      let bestPosition = { x: 0, y: 0 };
      let minY = Infinity;
      
      // 모든 가능한 시작 위치 탐색
      for (let y = 0; y < 50; y++) { // 50행까지만 탐색 (충분히 큰 값)
        for (let x = 0; x <= cols - itemWidth; x++) {
          let canPlace = true;
          
          // 해당 위치에 아이템을 배치할 수 있는지 확인
          for (let dy = 0; dy < itemHeight; dy++) {
            for (let dx = 0; dx < itemWidth; dx++) {
              if (gridOccupancyMap[y + dy][x + dx]) {
                canPlace = false;
                break;
              }
            }
            if (!canPlace) break;
          }
          
          // 배치 가능하고 현재까지 찾은 최적 위치보다 더 위쪽인 경우
          if (canPlace && y < minY) {
            minY = y;
            bestPosition = { x, y };
            
            // 만약 최상단(y=0)을 찾았다면 더 이상 찾을 필요 없음
            if (y === 0) break;
          }
        }
        
        // 최상단 위치를 찾았다면 더 아래 행은 확인할 필요 없음
        if (minY === 0) break;
      }
      
      // 최적의 위치에 아이템 배치
      const { x, y } = bestPosition;
      
      // 새 위치에 항목 추가
      newLayout.push({
        ...item,
        x,
        y
      });
      
      // 점유 그리드 맵 업데이트
      for (let dy = 0; dy < itemHeight; dy++) {
        for (let dx = 0; dx < itemWidth; dx++) {
          gridOccupancyMap[y + dy][x + dx] = true;
        }
      }
      
      // 현재 행의 높이 업데이트
      for (let dy = 0; dy < itemHeight; dy++) {
        const rowY = y + dy;
        if (!rowHeights.has(rowY) || rowHeights.get(rowY) < x + itemWidth) {
          rowHeights.set(rowY, x + itemWidth);
        }
      }
    });
    
    // 레이아웃 상태 업데이트
    setLayouts({ lg: newLayout });
    
    // 성공 메시지 표시 제거 (팝업 비활성화)
    // showSuccessMessage('대시보드 레이아웃이 자동으로 정렬되었습니다.');
  } catch (err) {
    console.error("[Dashboard] 레이아웃 자동 정렬 중 오류:", err);
    showErrorMessage('레이아웃 자동 정렬 중 오류가 발생했습니다.');
  }
};

/**
 * 정렬 및 크기 자동 조정 옵션 메뉴 표시 함수
 */
export const showArrangeOptions = (event, autoArrangeLayout, autoResizeComponents) => {
  event.preventDefault();
  
  // 옵션 메뉴 생성
  const optionsMenu = document.createElement('div');
  optionsMenu.className = 'arrange-options-menu';
  optionsMenu.innerHTML = `
    <div class="arrange-options-header">정렬 및 크기 조정</div>
    <div class="arrange-options-section-title">정렬 방식</div>
    <div class="arrange-option" data-option="position">현재 위치 기준</div>
    <div class="arrange-option" data-option="size_desc">큰 컴포넌트부터</div>
    <div class="arrange-option" data-option="size_asc">작은 컴포넌트부터</div>
    <div class="arrange-option" data-option="width">너비 기준</div>
    <div class="arrange-option" data-option="height">높이 기준</div>
    <div class="arrange-option" data-option="title">타이틀 기준</div>
    <div class="arrange-options-divider"></div>
    <div class="arrange-options-section-title">크기 자동 조정</div>
    <div class="arrange-option" data-option="uniform_size">균일한 크기로 조정</div>
    <div class="arrange-option" data-option="proportional_resize">비례 크기 조정</div>
    <div class="arrange-option" data-option="square_grid">정사각형 그리드로 조정</div>
    <div class="arrange-option" data-option="optimize_by_type">콘텐츠 타입별 최적화</div>
    <div class="arrange-option" data-option="fit_to_grid">그리드에 맞게 조정</div>
    <div class="arrange-option" data-option="reset_size">원래 크기로 복원</div>
  `;
  
  // 메뉴 위치 설정 (버튼 아래에 표시)
  const buttonRect = event.currentTarget.getBoundingClientRect();
  optionsMenu.style.position = 'absolute';
  optionsMenu.style.top = `${buttonRect.bottom + window.scrollY}px`;
  optionsMenu.style.left = `${buttonRect.left + window.scrollX}px`;
  
  // 메뉴 추가
  document.body.appendChild(optionsMenu);
  
  // 옵션 클릭 이벤트 리스너
  const options = optionsMenu.querySelectorAll('.arrange-option');
  options.forEach(option => {
    const handleOptionClick = () => {
      const selectedOption = option.getAttribute('data-option');
      
      // 크기 조정 옵션 처리
      if (['uniform_size', 'optimize_by_type', 'fit_to_grid', 'reset_size'].includes(selectedOption)) {
        autoResizeComponents(selectedOption);
      } else {
        // 기존 정렬 옵션 처리
        autoArrangeLayout(selectedOption);
      }
      
      optionsMenu.remove();
    };
    
    option.addEventListener('click', handleOptionClick, { passive: true });
  });
  
  // 외부 클릭 시 메뉴 닫기
  const handleClickOutside = (e) => {
    if (!optionsMenu.contains(e.target) && e.target !== event.currentTarget) {
      optionsMenu.remove();
      document.removeEventListener('click', handleClickOutside, { passive: true });
    }
  };
  
  // 약간의 지연 후 이벤트 리스너 등록 (메뉴 표시 직후 바로 닫히는 것 방지)
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside, { passive: true });
  }, 100);
};

/**
 * 컴포넌트 크기 자동 조정 함수
 */
export const autoResizeComponents = (
  option,
  dashboardTemplate,
  layouts,
  setLayouts,
  editMode,
  showSuccessMessage,
  showErrorMessage
) => {
  if (!dashboardTemplate || !dashboardTemplate.components || !layouts.lg) {
    console.error("[Dashboard] 컴포넌트 크기 조정을 위한 데이터가 없습니다.");
    return;
  }
  
  try {
    // 현재 레이아웃 항목 복제
    const currentLayoutItems = [...layouts.lg];
    const updatedLayout = [];
    
    // 컴포넌트 정보 매핑
    const componentsMap = {};
    dashboardTemplate.components.forEach(comp => {
      componentsMap[comp.id] = comp;
    });
    
    // 고정된 컴포넌트와 크기 조정 가능한 컴포넌트 분리
    const lockedItems = [];
    const resizableItems = [];
    
    currentLayoutItems.forEach(item => {
      const component = componentsMap[item.i];
      if (component && component.layout_locked === true) {
        lockedItems.push(item);
      } else {
        resizableItems.push(item);
      }
    });
    
    // 옵션에 따라 크기 조정 수행
    try {
      switch (option) {
      case 'uniform_size':
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        // 크기 조정 가능한 컴포넌트만 균일한 크기로 조정 (3x3 정사각형)
        resizableItems.forEach(item => {
          updatedLayout.push({
            ...item,
            w: 3, // 표준 너비
            h: 3, // 정사각형을 위해 높이도 동일하게
            minW: 2,
            minH: 2
          });
        });
        break;
        
      case 'square_grid':
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        // 정사각형 그리드에 맞게 크기 조정 가능한 컴포넌트를 정사각형으로 조정
        resizableItems.forEach(item => {
          const size = Math.max(item.w, item.h); // 더 큰 값을 기준으로
          updatedLayout.push({
            ...item,
            w: size,
            h: size, // 정사각형으로 만들기
            minW: 1,
            minH: 1
          });
        });
        break;
        
      case 'proportional_resize':
        // 컬럼 수 변화에 비례해서 차트 크기와 위치 모두 조정 (기존 비율 유지)
        const resizeRatio = dashboardTemplate?._resizeRatio || 1.0;
        const newColumns = dashboardTemplate?.layout?.columns || 12;
        
        // 비율이 1.0이면 조정할 필요 없음
        if (Math.abs(resizeRatio - 1.0) < 0.001) {
          currentLayoutItems.forEach(item => {
            updatedLayout.push({ ...item });
          });
          // 조정 생략 시에도 레이아웃 업데이트 후 성공 콜백 호출
          setLayouts({ lg: updatedLayout });
          if (showSuccessMessage) {
            // showSuccessMessage('차트 크기 조정이 완료되었습니다.'); // 모달 제거
          }
          return;
        }
        
        // 레이아웃 아이템이 없으면 조정 생략
        if (!currentLayoutItems || currentLayoutItems.length === 0) {
          if (showSuccessMessage) {
            // showSuccessMessage('조정할 차트가 없습니다.'); // 모달 제거
          }
          return;
        }
        
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        // 크기 조정 가능한 컴포넌트만 비례 조정
        resizableItems.forEach(item => {
          try {
            // 안전 장치: 필수 속성 확인
            if (!item || typeof item.x !== 'number' || typeof item.y !== 'number' || 
                typeof item.w !== 'number' || typeof item.h !== 'number') {
              console.error(`[autoResizeComponents] 유효하지 않은 아이템:`, item);
              updatedLayout.push({ ...item });
              return;
            }
            
            // 위치와 크기 모두 비례적으로 조정
            const newX = Math.round(item.x * resizeRatio);
            const newY = Math.round(item.y * resizeRatio);
            const newWidth = Math.round(item.w * resizeRatio);
            const newHeight = Math.round(item.h * resizeRatio);
            
            // 최소 크기 보장 및 최대 크기 제한
            const adjustedX = Math.max(0, Math.min(newX, newColumns - 1));
            const adjustedWidth = Math.max(1, Math.min(newWidth, newColumns - adjustedX));
            const adjustedHeight = Math.max(1, newHeight);
            
            updatedLayout.push({
              ...item,
              x: adjustedX,
              y: newY,
              w: adjustedWidth,
              h: adjustedHeight,
              minW: 1,
              minH: 1
            });
            
          } catch (error) {
            console.error(`[autoResizeComponents] 아이템 처리 중 오류:`, item, error);
            updatedLayout.push({ ...item });
          }
        });
        
        // 비례 조정 완료 후 레이아웃 업데이트
        setLayouts({ lg: updatedLayout });
        // 성공 콜백 호출 (Dashboard.js에서 전달한 콜백 함수 실행)
        if (showSuccessMessage) {
          // showSuccessMessage(); // 모달 제거
        }
        return;
        
      case 'optimize_by_type':
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        // 콘텐츠 타입에 따라 최적의 크기 적용 (크기 조정 가능한 컴포넌트만)
        resizableItems.forEach(item => {
          const component = componentsMap[item.i];
          let optimalWidth = 3;
          let optimalHeight = 2;
          
          if (component) {
            // 컴포넌트 타입 확인
            const isStatic = component.data_source?.type === 'static';
            const vizType = component.chart_options?.viz_type || '';
            
            // 차트 타입에 따라 적절한 크기 설정 (정사각형 그리드 고려)
            if (vizType === 'markdown' || isStatic) {
              // 마크다운이나 정적 콘텐츠는 작은 정사각형으로
              optimalWidth = 3;
              optimalHeight = 3;
            } else if (vizType === 'bar') {
              // 막대 차트는 약간 넓게 하되 비율 유지
              optimalWidth = 4;
              optimalHeight = 3;
            } else if (vizType === 'pie' || vizType === 'doughnut') {
              // 원형 차트는 정사각형으로
              optimalWidth = 3;
              optimalHeight = 3;
            } else if (vizType === 'line' || vizType === 'time_series') {
              // 선형 차트는 약간 넓게 하되 비율 유지
              optimalWidth = 4;
              optimalHeight = 3;
            } else if (vizType === 'table') {
              // 테이블은 크게
              optimalWidth = 4;
              optimalHeight = 4;
            } else if (vizType === 'heatmap') {
              // 히트맵은 정사각형으로
              optimalWidth = 4;
              optimalHeight = 4;
            }
          }
          
          updatedLayout.push({
            ...item,
            w: optimalWidth,
            h: optimalHeight,
            minW: 2,
            minH: 2
          });
        });
        break;
        
      case 'fit_to_grid':
        // 그리드에 맞게 조정 (템플릿 컬럼 수에 맞춰 분배)
        const cols = dashboardTemplate?.layout?.columns || 12;
        const itemCount = resizableItems.length; // 크기 조정 가능한 컴포넌트만 카운트
        
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        if (itemCount > 0) {
          // 크기 조정 가능한 아이템을 정렬된 상태로 처리 - 현재 위치 기준 정렬
          const sortedItems = [...resizableItems].sort((a, b) => {
            if (a.y === b.y) return a.x - b.x;
            return a.y - b.y;
          });
          
          // 최적의 그리드 레이아웃 계산
          let itemsPerRow;
          let rowCount;
          
          // 항목 수에 따라 최적의 그리드 구성 찾기
          if (itemCount <= 3) {
            itemsPerRow = itemCount; // 3개 이하면 한 줄에 모두 표시
            rowCount = 1;
          } else if (itemCount <= 4) {
            itemsPerRow = 2; // 4개면 2x2 그리드
            rowCount = 2;
          } else if (itemCount <= 6) {
            itemsPerRow = 3; // 5-6개면 3x2 그리드
            rowCount = 2;
          } else if (itemCount <= 9) {
            itemsPerRow = 3; // 7-9개면 3x3 그리드
            rowCount = 3;
          } else if (itemCount <= 12) {
            itemsPerRow = 4; // 10-12개면 4x3 그리드
            rowCount = 3;
          } else {
            // 12개 초과 시 자동 계산
            itemsPerRow = 4;
            rowCount = Math.ceil(itemCount / itemsPerRow);
          }
          
          // 각 아이템의 최적 너비 계산 (여백 고려)
          const itemWidth = Math.floor(cols / itemsPerRow);
          
          // 각 아이템을 균일한 그리드에 배치
          sortedItems.forEach((item, index) => {
            const row = Math.floor(index / itemsPerRow);
            const col = index % itemsPerRow;
            
            updatedLayout.push({
              ...item,
              x: col * itemWidth,
              y: row * 2, // 행 간격을 일정하게 유지
              w: itemWidth,
              h: 2,       // 모든 항목 높이 통일
              minW: 2,
              minH: 2
            });
          });
          
        }
        
        // fit_to_grid 옵션은 이미 위치가 조정되었으므로 바로 상태 업데이트
        setLayouts({ lg: updatedLayout });
        
        // return updatedLayout; // 반환 값 제거
        
      case 'reset_size':
        // 고정된 컴포넌트는 그대로 유지
        lockedItems.forEach(item => {
          updatedLayout.push({ ...item });
        });
        
        // 원래 크기로 복원 (대시보드 템플릿의 position 정보 사용)
        // 위치(x, y)는 현재 레이아웃 그대로 유지하고 크기(w, h)만 원래대로 복원
        resizableItems.forEach(item => {
          const component = componentsMap[item.i];
          
          if (component && component.position) {
            updatedLayout.push({
              ...item,
              // x, y 값은 현재 레이아웃의 값을 유지
              w: component.position.w || 3,
              h: component.position.h || 2,
              minW: 2,
              minH: 2
            });
          } else {
            // 원본 position 정보가 없으면 기본 크기 사용하고 위치는 유지
            updatedLayout.push({
              ...item,
              w: 3,
              h: 2,
              minW: 2,
              minH: 2
            });
          }
        });
        
        // reset_size 옵션은 위치 변경 없이 크기만 조정하고 바로 상태 업데이트
        setLayouts({ lg: updatedLayout });
        
        // return updatedLayout; // 반환 값 제거
        
              default:
          console.error("[Dashboard] 알 수 없는 크기 조정 옵션:", option);
          return;
      }
    } catch (error) {
      console.error("[autoResizeComponents] 크기 조정 중 오류 발생:", error);
      showErrorMessage(`차트 크기 조정 중 오류가 발생했습니다: ${error.message}`);
      return;
    }
    
    // 이 부분은 uniform_size와 optimize_by_type 옵션에 대해서만 실행됨
    // 크기 조정 후 자동 정렬 수행 (고정된 컴포넌트는 제외하고)
    const cols = dashboardTemplate?.layout?.columns || 12;
    
    // 새 레이아웃 기반으로 그리드 맵 생성 및 최적 위치 찾기
    const gridOccupancyMap = [];
    for (let y = 0; y < 100; y++) {
      gridOccupancyMap[y] = new Array(cols).fill(false);
    }
    
    const finalLayout = [];
    
    // 고정된 컴포넌트들을 먼저 그리드 맵에 표시하고 final 레이아웃에 추가
    const lockedComponentsInUpdated = updatedLayout.filter(item => {
      const component = componentsMap[item.i];
      return component && component.layout_locked === true;
    });
    
    const resizableComponentsInUpdated = updatedLayout.filter(item => {
      const component = componentsMap[item.i];
      return !(component && component.layout_locked === true);
    });
    
    // 고정된 컴포넌트 먼저 처리 (그리드 맵에 표시하지 않음 - 투명 취급)
    lockedComponentsInUpdated.forEach(item => {
      finalLayout.push({ ...item });
      
      // 고정된 컴포넌트가 점유하는 영역을 그리드 맵에 표시하지 않음 (투명 취급)
      // for (let dy = 0; dy < item.h; dy++) {
      //   for (let dx = 0; dx < item.w; dx++) {
      //     const y = item.y + dy;
      //     const x = item.x + dx;
      //     if (y < 100 && x < cols) {
      //       gridOccupancyMap[y][x] = true;
      //     }
      //   }
      // }
    });
    
    // 크기 조정된 컴포넌트들을 하나씩 추가 (고정된 컴포넌트는 무시)
    resizableComponentsInUpdated.forEach(item => {
      const itemWidth = item.w;
      const itemHeight = item.h;
      
      // 최적의 위치 찾기
      let bestPosition = { x: 0, y: 0 };
      let minY = Infinity;
      
      for (let y = 0; y < 50; y++) {
        for (let x = 0; x <= cols - itemWidth; x++) {
          let canPlace = true;
          
          for (let dy = 0; dy < itemHeight; dy++) {
            for (let dx = 0; dx < itemWidth; dx++) {
              if (gridOccupancyMap[y + dy][x + dx]) {
                canPlace = false;
                break;
              }
            }
            if (!canPlace) break;
          }
          
          if (canPlace && y < minY) {
            minY = y;
            bestPosition = { x, y };
            
            if (y === 0) break;
          }
        }
        
        if (minY === 0) break;
      }
      
      // 최적의 위치에 항목 배치
      const { x, y } = bestPosition;
      
      finalLayout.push({
        ...item,
        x,
        y
      });
      
      // 점유 그리드 맵 업데이트
      for (let dy = 0; dy < itemHeight; dy++) {
        for (let dx = 0; dx < itemWidth; dx++) {
          gridOccupancyMap[y + dy][x + dx] = true;
        }
      }
    });
    
    // 레이아웃 상태 업데이트
    setLayouts({ lg: finalLayout });
    
    // 성공 메시지 표시하지 않음 (팝업 제거)
    // showSuccessMessage('컴포넌트 크기가 자동으로 조정되었습니다.');
  } catch (err) {
    console.error("[Dashboard] 컴포넌트 크기 조정 중 오류:", err);
    showErrorMessage('컴포넌트 크기 조정 중 오류가 발생했습니다.');
  }
}; 